import { useEffect, useState } from 'react';
import { useLazyQuery, gql } from '@apollo/client';
import { useSessionStorageState } from '../utils/useSessionStorageState';
import { lookupStorageKey, initialState } from '../apollo/cache';

// Define the GraphQL query for checking delivery status externally
const CHECK_STATUS = gql`
  query DeliveryStatus($carrier: String!, $tracking_number: String!) {
    deliveryStatus(carrier: $carrier, tracking_number: $tracking_number) {
      status
      attempt
      last_detail {
        datetime
        status
        message
      }
    }
  }
`;

const CLOSED_FOLDER_ID = 435733;

const useLostInTransit = () => {
  const [state] = useSessionStorageState(lookupStorageKey, initialState);
  const [isLostInTransit, setIsLostInTransit] = useState(false);

  // Lazy query to fetch delivery status if not found in metadata
  const [fetchDeliveryStatus, { data, loading, error  }] = useLazyQuery(CHECK_STATUS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.deliveryStatus) {
        evaluateLostInTransit(data.deliveryStatus);
      }
    },
    onError: (error) => {
      console.error('Error fetching delivery status:', error);
    },
  });

  // Function to check if an order is lost in transit
  const checkLostInTransitStatus = (shipment, metadata, folderId) => {
    let deliveryStatus = metadata?.find(v => v.key === 'delivery_status')?.value;

    if (deliveryStatus) {
      try {
        const parsedStatus = JSON.parse(deliveryStatus);
        evaluateLostInTransit(parsedStatus);
      } catch (error) {
        console.error('Error parsing delivery status from metadata:', error);
      }
    }

    // If delivery status is not found in metadata, fetch it from external API
    if (shipment?.tracking_number && shipment?.carrier_code) {
      fetchDeliveryStatus({
        variables: {
          carrier: shipment.carrier_code,
          tracking_number: shipment.tracking_number,
        },
      });
    }
  };

  // Function to evaluate if an order is lost in transit based on delivery data
  const evaluateLostInTransit = (statusData) => {
    if (!statusData?.last_detail?.datetime) return;

    const lastUpdateDate = new Date(statusData.last_detail.datetime);
    const currentDate = new Date();

    if (isNaN(lastUpdateDate.getTime())) {
      console.error('Invalid last update date');
      return;
    }

    const daysDifference = Math.floor(
      (currentDate.getTime() - lastUpdateDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    
    // Check if the order is in the "Closed" folder and has no recent updates
    const lostInTransit =
    state?.order?.folder_id === CLOSED_FOLDER_ID &&
    daysDifference >= 7 &&
    String(statusData.status).toLowerCase() !== 'delivered';
  
    setIsLostInTransit(lostInTransit);
  };

  useEffect(() => {
    if (!state?.order?.order_shipments || !state?.order?.order_metadata) return;

    const shipment = state.order.order_shipments[0];
    const metadata = state.order.order_metadata;
    const folderId = state.order.folder_id;

    checkLostInTransitStatus(shipment, metadata, folderId);
  }, [state]);

  return isLostInTransit;
};

export default useLostInTransit;
